import { createFileRoute } from "@tanstack/react-router";
import { useState, useRef, useEffect } from "react";
import wordmarkUrl from "@/assets/wordmark.png";
import stripUrl from "@/assets/strip.png";

export const Route = createFileRoute("/")({
  component: EnrolPage,
});

const API_BASE = (import.meta.env.VITE_API_BASE_URL as string) ?? "https://api.stockclerk.app";
const TENANT_SLUG = "the-little-green-hound-pantry";
const ENROLL_URL = `${API_BASE}/api/loyalty/enroll`;
const EMAIL_PASS_URL = `${API_BASE}/api/loyalty/email-pass`;

const REWARDS = [
  { pts: 350, label: "£10 off" },
  { pts: 850, label: "£25 off" },
  { pts: 1500, label: "£50 off" },
];

function rewardFootnote(balance: number): string {
  if (balance >= 1500) return "Reward available! Use it next visit.";
  const next = REWARDS.find((r) => balance < r.pts)!;
  const away = (next.pts - balance).toLocaleString("en-GB");
  return `${away} points away from ${next.label}`;
}

function isIOS(): boolean {
  if (typeof navigator === "undefined") return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
}

function isAndroid(): boolean {
  if (typeof navigator === "undefined") return false;
  return /Android/i.test(navigator.userAgent);
}

type FormValues = {
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
  agree: boolean;
};

type SuccessState = {
  kind: "success";
  isReissue: boolean;
  pointsBalance: number;
  firstName: string;
  passBlobUrl: string | null; // null when 202 (deferred)
  passDeferred: boolean;
  serialNumber?: string;
};

type View =
  | { kind: "form" }
  | SuccessState
  | { kind: "error"; message: string };

function EnrolPage() {
  const [values, setValues] = useState<FormValues>({
    email: "",
    firstName: "",
    lastName: "",
    phone: "",
    agree: false,
  });
  const [fieldErrors, setFieldErrors] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [view, setView] = useState<View>({ kind: "form" });
  const submittingRef = useRef(false);
  const headingRef = useRef<HTMLHeadingElement | null>(null);

  // Move keyboard focus to the new screen heading when view changes (a11y).
  useEffect(() => {
    if (view.kind !== "form" && headingRef.current) {
      headingRef.current.focus();
    }
  }, [view.kind]);

  function update<K extends keyof FormValues>(k: K, v: FormValues[K]) {
    setValues((prev) => ({ ...prev, [k]: v }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (submittingRef.current) return;

    setFieldErrors([]);

    const localErrors: string[] = [];
    if (!values.email.trim()) localErrors.push("Email is required");
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email))
      localErrors.push("Please enter a valid email");
    if (!values.firstName.trim()) localErrors.push("First name is required");
    if (!values.agree) localErrors.push("You must agree to the terms");
    if (localErrors.length) {
      setFieldErrors(localErrors);
      return;
    }

    submittingRef.current = true;
    setSubmitting(true);

    const body: Record<string, string> = {
      tenantSlug: TENANT_SLUG,
      email: values.email.trim(),
      firstName: values.firstName.trim(),
    };
    if (values.lastName.trim()) body.lastName = values.lastName.trim();
    if (values.phone.trim()) body.phone = values.phone.trim();

    try {
      const res = await fetch(ENROLL_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/vnd.apple.pkpass, application/json" },
        body: JSON.stringify(body),
      });

      const ct = res.headers.get("content-type") ?? "";

      if (res.status === 200 && ct.includes("application/vnd.apple.pkpass")) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        // For 200 binary, we can't read points balance — treat as new customer per spec.
        setView({
          kind: "success",
          isReissue: false,
          pointsBalance: 0,
          firstName: values.firstName.trim(),
          passBlobUrl: url,
          passDeferred: false,
        });
      } else if (res.status === 202) {
        const data = await res.json();
        setView({
          kind: "success",
          isReissue: !!data.isReissue || (data.pointsBalance ?? 0) > 0,
          pointsBalance: data.pointsBalance ?? 0,
          firstName: values.firstName.trim(),
          passBlobUrl: null,
          passDeferred: true,
          serialNumber: data.serialNumber,
        });
      } else if (res.status === 400) {
        const data = await res.json().catch(() => ({}));
        setFieldErrors(Array.isArray(data.errors) ? data.errors : ["Please check your details"]);
      } else if (res.status === 503) {
        setView({ kind: "error", message: "Hound Club enrolment is temporarily unavailable. Please try again shortly." });
      } else {
        setView({ kind: "error", message: "We couldn't issue your pass right now. Please try again, or email help@thelittlegreenhoundpantry.co.uk" });
      }
    } catch {
      setView({ kind: "error", message: "Network error — please check your connection and try again." });
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  }

  function resetToForm() {
    setView({ kind: "form" });
  }

  async function emailPass() {
    const body: Record<string, string> = {
      tenantSlug: TENANT_SLUG,
      email: values.email.trim(),
      firstName: values.firstName.trim(),
    };
    if (values.lastName.trim()) body.lastName = values.lastName.trim();
    if (values.phone.trim()) body.phone = values.phone.trim();

    const res = await fetch(EMAIL_PASS_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(data.error || `HTTP ${res.status}`);
    }
  }

  function downloadPass(blobUrl: string) {
    if (isIOS()) {
      // iOS Safari: navigating to the blob with correct MIME triggers Wallet.
      window.location.href = blobUrl;
    } else {
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = "houndclub.pkpass";
      document.body.appendChild(a);
      a.click();
      a.remove();
    }
  }

  return (
    <div className="min-h-screen" style={{ background: "#f4f1ec" }}>
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:left-3 focus:top-3 focus:rounded focus:bg-white focus:px-3 focus:py-2 focus:text-sm focus:shadow"
      >
        Skip to content
      </a>
      <main id="main" className="mx-auto w-full max-w-[420px] px-4 py-6 sm:py-10">
        {view.kind === "form" && (
          <>
            <FormCard
              values={values}
              update={update}
              errors={fieldErrors}
              submitting={submitting}
              onSubmit={handleSubmit}
            />
            <HowItWorks />
            <FAQ />
          </>
        )}
        {view.kind === "success" && (
          <SuccessCard
            view={view}
            headingRef={headingRef}
            onAdd={() => view.passBlobUrl && downloadPass(view.passBlobUrl)}
            onEmail={emailPass}
            emailAddress={values.email.trim()}
          />
        )}
        {view.kind === "error" && (
          <ErrorCard message={view.message} headingRef={headingRef} onRetry={resetToForm} />
        )}

        <p className="mt-8 text-center text-xs text-neutral-500">
          The Little Green Hound Pantry · Kensington, London
        </p>
      </main>
    </div>
  );
}

/* ---------- Cards ---------- */

const GREEN = "rgb(67, 87, 63)";
const GREEN_DARK = "rgb(50, 67, 47)";
const LABEL = "rgb(220, 230, 215)";

/**
 * Mirrors the real Apple Wallet .pkpass layout so the in-app preview always
 * matches what the customer actually receives. Do not redesign without
 * updating the pass template in StockClerk in lockstep.
 */
function PassHeader({
  memberName,
  pointsBalance,
  nextReward,
}: {
  memberName?: string;
  pointsBalance?: number;
  nextReward?: string;
}) {
  const points = pointsBalance ?? 0;
  return (
    <div style={{ background: GREEN }} className="rounded-t-3xl overflow-hidden">
      {/* Logo row + POINTS field — matches pass header.
          Logo mirrors the thelittlegreenhoundpantry.co.uk wordmark. */}
      <div className="flex items-center justify-between gap-3 px-5 pt-4 pb-3">
        <img
          src={wordmarkUrl}
          alt="The Little Green Hound Pantry"
          className="h-10 w-auto max-w-[78%] object-contain object-left shrink"
        />
        <div className="text-right shrink-0">
          <div className="text-[10px] tracking-wider font-medium" style={{ color: LABEL }}>
            POINTS
          </div>
          <div className="text-white text-base font-normal leading-tight">
            {points.toLocaleString("en-GB")}
          </div>
        </div>
      </div>
      {/* Strip image */}
      <div
        className="w-full"
        style={{
          aspectRatio: "375 / 144",
          backgroundImage: `url(${stripUrl})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />
      {/* Primary fields row — MEMBER + NEXT REWARD */}
      <div className="flex items-start justify-between gap-4 px-5 pt-3 pb-5">
        <div className="min-w-0">
          <div className="text-[10px] tracking-wider font-medium" style={{ color: LABEL }}>
            MEMBER
          </div>
          <div className="text-white text-base font-semibold truncate">
            {memberName?.trim() ? memberName : "Your name"}
          </div>
        </div>
        <div className="text-right min-w-0">
          <div className="text-[10px] tracking-wider font-medium" style={{ color: LABEL }}>
            NEXT REWARD
          </div>
          <div className="text-white text-base font-semibold truncate">
            {nextReward ?? "350 pts to £10 off"}
          </div>
        </div>
      </div>
    </div>
  );
}

function nextRewardLabel(balance: number): string {
  if (balance >= 1500) return "Reward ready";
  const next = REWARDS.find((r) => balance < r.pts)!;
  const away = (next.pts - balance).toLocaleString("en-GB");
  return `${away} pts to ${next.label}`;
}

function FormCard({
  values, update, errors, submitting, onSubmit,
}: {
  values: FormValues;
  update: <K extends keyof FormValues>(k: K, v: FormValues[K]) => void;
  errors: string[];
  submitting: boolean;
  onSubmit: (e: React.FormEvent) => void;
}) {
  const [showAndroidNote, setShowAndroidNote] = useState(false);
  useEffect(() => {
    setShowAndroidNote(isAndroid());
  }, []);
  return (
    <section className="overflow-hidden rounded-3xl bg-white shadow-[0_10px_30px_rgba(0,0,0,.08)]">
      <PassHeader
        memberName={[values.firstName, values.lastName].filter(Boolean).join(" ").trim()}
        pointsBalance={0}
        nextReward="350 pts to £10 off"
      />
      <form onSubmit={onSubmit} className="space-y-4 px-5 py-6" noValidate>
        {showAndroidNote && (
          <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
            <strong className="font-semibold">On Android?</strong> Your pass works in
            Apple Wallet on iPhone. We'll still create your account and email a
            backup link — just show your email at the till to earn points.
          </div>
        )}
        <Field
          label="Email"
          required
          type="email"
          inputMode="email"
          autoComplete="email"
          value={values.email}
          onChange={(v) => update("email", v)}
          helper="We'll find your existing balance if you've shopped with us before."
        />
        <Field
          label="First name"
          required
          autoComplete="given-name"
          value={values.firstName}
          onChange={(v) => update("firstName", v)}
        />
        <Field
          label="Last name"
          autoComplete="family-name"
          value={values.lastName}
          onChange={(v) => update("lastName", v)}
        />
        <Field
          label="Mobile"
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          value={values.phone}
          onChange={(v) => update("phone", v)}
          helper="For receipt links — never used for marketing."
        />

        <label className="flex items-start gap-3 pt-2 text-sm leading-snug text-neutral-700">
          <input
            type="checkbox"
            checked={values.agree}
            onChange={(e) => update("agree", e.target.checked)}
            className="mt-0.5 h-5 w-5 shrink-0 accent-[rgb(67,87,63)]"
            aria-describedby="agree-text"
          />
          <span id="agree-text">
            I agree to the Hound Club{" "}
            <a href="/terms" className="underline">terms</a> and{" "}
            <a href="/privacy" className="underline">privacy policy</a>.
          </span>
        </label>

        <div role="alert" aria-live="polite" aria-atomic="true">
          {errors.length > 0 && (
            <ul className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
              {errors.map((e, i) => (
                <li key={i}>• {e}</li>
              ))}
            </ul>
          )}
        </div>

        <button
          type="submit"
          disabled={submitting}
          aria-busy={submitting}
          style={{ background: GREEN }}
          className="mt-2 flex h-12 w-full items-center justify-center rounded-xl text-base font-semibold text-white transition-opacity disabled:opacity-60"
        >
          {submitting ? "Issuing your pass…" : "Get my pass"}
        </button>
      </form>
    </section>
  );
}

function Field({
  label, required, helper, value, onChange, type = "text", inputMode, autoComplete,
}: {
  label: string;
  required?: boolean;
  helper?: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  inputMode?: "text" | "email" | "tel" | "numeric";
  autoComplete?: string;
}) {
  return (
    <div>
      <label className="mb-1 block text-sm font-medium text-neutral-800">
        {label} {required && <span className="text-neutral-400">*</span>}
      </label>
      <input
        type={type}
        inputMode={inputMode}
        autoComplete={autoComplete}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-12 w-full rounded-xl border border-neutral-300 bg-white px-3 text-base outline-none focus:border-[rgb(67,87,63)] focus:ring-2 focus:ring-[rgb(67,87,63)]/20"
      />
      {helper && <p className="mt-1 text-xs text-neutral-500">{helper}</p>}
    </div>
  );
}

function SuccessCard({
  view,
  onAdd,
  onEmail,
  emailAddress,
  headingRef,
}: {
  view: SuccessState;
  onAdd: () => void;
  onEmail: () => Promise<void>;
  emailAddress: string;
  headingRef: React.RefObject<HTMLHeadingElement | null>;
}) {
  const [emailState, setEmailState] = useState<"idle" | "sending" | "sent" | "error">("idle");
  const [emailErrMsg, setEmailErrMsg] = useState<string | null>(null);

  async function handleEmailClick() {
    setEmailState("sending");
    setEmailErrMsg(null);
    try {
      await onEmail();
      setEmailState("sent");
    } catch (err) {
      setEmailState("error");
      setEmailErrMsg(err instanceof Error ? err.message : "Failed");
    }
  }

  const headline = view.isReissue ? `Welcome back, ${view.firstName}` : "Welcome to Hound Club";
  const subhead = view.isReissue ? "Your existing balance is ready." : "You're enrolled.";
  const balanceLabel = view.isReissue
    ? `${view.pointsBalance.toLocaleString("en-GB")} points`
    : "0 points to start";
  const footnote = view.isReissue
    ? rewardFootnote(view.pointsBalance)
    : "Show your pass to our team at the till to earn points on every purchase.";

  return (
    <section className="overflow-hidden rounded-3xl bg-white shadow-[0_10px_30px_rgba(0,0,0,.08)]">
      <PassHeader
        memberName={view.firstName}
        pointsBalance={view.pointsBalance}
        nextReward={nextRewardLabel(view.pointsBalance)}
      />
      <div className="px-6 py-7 text-center">
        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full" style={{ background: GREEN }}>
          <svg viewBox="0 0 24 24" className="h-8 w-8 text-white" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
            <path d="M5 12l5 5L20 7" />
          </svg>
        </div>
        <h1
          ref={headingRef}
          tabIndex={-1}
          className="text-2xl font-semibold text-neutral-900 outline-none"
        >
          {headline}
        </h1>
        <p className="mt-1 text-neutral-600">{subhead}</p>

        <div
          className="mx-auto mt-5 inline-block rounded-full px-4 py-1.5 text-sm font-medium"
          style={{ background: "#eef2ea", color: GREEN_DARK }}
        >
          {balanceLabel}
        </div>

        <div className="mt-6 space-y-3">
          {view.passDeferred ? (
            <p className="rounded-xl bg-neutral-100 px-4 py-3 text-sm text-neutral-700">
              Your pass is being prepared — we'll email it shortly.
            </p>
          ) : (
            <>
              <button
                onClick={onAdd}
                style={{ background: "#000" }}
                className="flex h-12 w-full items-center justify-center gap-2 rounded-xl text-base font-semibold text-white"
              >
                <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor">
                  <path d="M16.365 1.43c0 1.14-.493 2.27-1.177 3.08-.744.9-1.99 1.57-2.987 1.493-.12-1.06.396-2.18 1.057-2.91.744-.83 2.005-1.45 3.107-1.66zm3.13 17.81c-.582 1.31-.84 1.89-1.59 3.05-1.04 1.6-2.51 3.6-4.32 3.61-1.61.02-2.02-1.05-4.21-1.04-2.18.01-2.64 1.06-4.25 1.04-1.81-.01-3.2-1.81-4.24-3.41-2.92-4.49-3.23-9.76-1.42-12.57 1.28-1.99 3.3-3.16 5.2-3.16 1.93 0 3.15 1.06 4.74 1.06 1.55 0 2.49-1.06 4.72-1.06 1.69 0 3.48.92 4.76 2.51-4.18 2.29-3.5 8.27 1.61 9.97z" />
                </svg>
                Add to Apple Wallet
              </button>
              <button
                onClick={handleEmailClick}
                disabled={emailState === "sending" || emailState === "sent"}
                aria-busy={emailState === "sending"}
                className="flex h-12 w-full items-center justify-center rounded-xl border border-neutral-300 bg-white text-base font-medium text-neutral-800 transition-opacity disabled:opacity-60"
              >
                {emailState === "idle" && "Email me the pass"}
                {emailState === "sending" && "Sending…"}
                {emailState === "sent" && `✓ Sent to ${emailAddress}`}
                {emailState === "error" && "Email failed — try again"}
              </button>
              {emailState === "error" && emailErrMsg && (
                <p role="alert" className="text-xs text-red-700">
                  {emailErrMsg}
                </p>
              )}
            </>
          )}
        </div>

        <p className="mt-6 text-sm text-neutral-600">{footnote}</p>
      </div>
    </section>
  );
}

function ErrorCard({
  message,
  onRetry,
  headingRef,
}: {
  message: string;
  onRetry: () => void;
  headingRef: React.RefObject<HTMLHeadingElement | null>;
}) {
  return (
    <section
      role="alert"
      aria-live="assertive"
      className="overflow-hidden rounded-3xl bg-white shadow-[0_10px_30px_rgba(0,0,0,.08)]"
    >
      <div className="px-6 py-7 text-center" style={{ background: "#a4453a", color: "#fff" }}>
        <h1
          ref={headingRef}
          tabIndex={-1}
          className="text-2xl font-semibold outline-none"
        >
          Hmm, that didn't work
        </h1>
      </div>
      <div className="px-6 py-7 text-center">
        <p className="text-neutral-700">{message}</p>
        <button
          onClick={onRetry}
          style={{ background: GREEN }}
          className="mt-6 flex h-12 w-full items-center justify-center rounded-xl text-base font-semibold text-white"
        >
          Try again
        </button>
      </div>
    </section>
  );
}

/* ---------- Supporting sections ---------- */

function HowItWorks() {
  const steps = [
    { n: 1, t: "Join in 30 seconds", d: "Pop in your email and name — no app to download." },
    { n: 2, t: "Add to Apple Wallet", d: "Your pass appears on your iPhone instantly." },
    { n: 3, t: "Earn on every visit", d: "Show your pass at the till. 1 point per £1 spent." },
  ];
  return (
    <section aria-labelledby="how-heading" className="mt-8 rounded-3xl bg-white px-6 py-7 shadow-[0_10px_30px_rgba(0,0,0,.05)]">
      <h2 id="how-heading" className="text-center text-lg font-semibold text-neutral-900">
        How it works
      </h2>
      <ol className="mt-5 space-y-4">
        {steps.map((s) => (
          <li key={s.n} className="flex gap-4">
            <div
              aria-hidden="true"
              style={{ background: GREEN }}
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-semibold text-white"
            >
              {s.n}
            </div>
            <div>
              <p className="text-sm font-semibold text-neutral-900">{s.t}</p>
              <p className="text-sm text-neutral-600">{s.d}</p>
            </div>
          </li>
        ))}
      </ol>
      <div className="mt-6 rounded-xl bg-[#eef2ea] px-4 py-3 text-sm text-[rgb(50,67,47)]">
        <strong className="font-semibold">Rewards:</strong> 350 pts = £10 off · 850 pts = £25 off · 1,500 pts = £50 off
      </div>
    </section>
  );
}

function FAQ() {
  const items = [
    {
      q: "Do I need to download an app?",
      a: "No. Your pass lives in Apple Wallet, which is already on your iPhone.",
    },
    {
      q: "I'm on Android — can I still join?",
      a: "Yes. We'll create your account and you can show your email at the till. We're working on Google Wallet support.",
    },
    {
      q: "I've shopped here before — will my points carry over?",
      a: "Yes. If we recognise your email, your existing balance loads automatically.",
    },
    {
      q: "How do I redeem rewards?",
      a: "Just let our team know at the till. We'll deduct the points and apply the discount.",
    },
    {
      q: "Do points expire?",
      a: "No. Your Hound Club points stay with you for as long as the programme runs.",
    },
  ];
  return (
    <section aria-labelledby="faq-heading" className="mt-6 rounded-3xl bg-white px-6 py-7 shadow-[0_10px_30px_rgba(0,0,0,.05)]">
      <h2 id="faq-heading" className="text-center text-lg font-semibold text-neutral-900">
        Common questions
      </h2>
      <div className="mt-4 divide-y divide-neutral-200">
        {items.map((it, i) => (
          <details key={i} className="group py-3">
            <summary className="flex cursor-pointer list-none items-center justify-between gap-3 text-sm font-medium text-neutral-900">
              <span>{it.q}</span>
              <span aria-hidden="true" className="text-neutral-400 transition-transform group-open:rotate-45">+</span>
            </summary>
            <p className="mt-2 text-sm text-neutral-600">{it.a}</p>
          </details>
        ))}
      </div>
      <p className="mt-5 text-center text-xs text-neutral-500">
        Need help? Email{" "}
        <a className="underline" href="mailto:help@thelittlegreenhoundpantry.co.uk">
          help@thelittlegreenhoundpantry.co.uk
        </a>
      </p>
    </section>
  );
}
